package org.moxultimate.topwithmox.wallpaper.ui

import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.wallpaper.api.WallpaperRepository
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.moxultimate.topwithmox.wallpaper.data.WallpaperSearchRequest

@Suppress("UNCHECKED_CAST")
class WallpaperViewModel(
    private val repository: WallpaperRepository,
) : ViewModel() {

    // ── Screen state ──────────────────────────────────────────────────────────

    private val _screenState = MutableStateFlow<WallpaperState>(WallpaperState.Loading)
    val screenState: StateFlow<WallpaperState> = _screenState

    private val _isLoadingMore = MutableStateFlow(false)
    val isLoadingMore: StateFlow<Boolean> = _isLoadingMore

    private val _hasMoreInCurrentView = MutableStateFlow(true)
    val hasMoreInCurrentView: StateFlow<Boolean> = _hasMoreInCurrentView

    // ── General list (no active filters) ─────────────────────────────────────

    private val accumulatedWallpapers = mutableListOf<WallpaperResponse>()
    private var lastId: Long = Long.MAX_VALUE
    private var hasMore = true

    // ── Filter chips ──────────────────────────────────────────────────────────

    /** Selected type chips (Phone / Desktop / Widescreen / Vertical). */
    private val _selectedTypes = MutableStateFlow<Set<String>>(emptySet())
    val selectedTypes: StateFlow<Set<String>> = _selectedTypes

    /** True while the 360° chip is on; keeps only wallpapers that have a panorama. */
    private val _only360 = MutableStateFlow(false)
    val only360: StateFlow<Boolean> = _only360

    /** Selected series chips. */
    private val _selectedSeries = MutableStateFlow<Set<String>>(emptySet())
    val selectedSeries: StateFlow<Set<String>> = _selectedSeries

    // ── Search text (typed value) and the query last sent to the server ───────

    private val _searchText = MutableStateFlow("")
    val searchText: StateFlow<String> = _searchText

    /** Query the current filtered results were fetched with; pagination keeps using it. */
    private var appliedQuery = ""
    private var filterJob: Job? = null

    // ── Filtered results (populated when any chip is active) ──────────────────

    private val filteredWallpapers = mutableListOf<WallpaperResponse>()
    private var filterBeforeId = Long.MAX_VALUE
    private var filterHasMore = true
    private var isFilterLoading = false

    private val isAnyFilterActive: Boolean
        get() = _selectedTypes.value.isNotEmpty() || _selectedSeries.value.isNotEmpty() ||
            _only360.value || appliedQuery.isNotBlank()

    // ── Series / Tags lists (for chips and autocomplete) ──────────────────────

    private val _allSeries = MutableStateFlow<List<String>>(emptyList())
    val allSeries: StateFlow<List<String>> = _allSeries

    private val _allTags = MutableStateFlow<List<String>>(emptyList())
    val allTags: StateFlow<List<String>> = _allTags

    init {
        loadAllWallpapers()
        loadAllSeries()
        loadAllTags()
    }

    // ── Search ────────────────────────────────────────────────────────────────

    /**
     * setSearchText
     * Updates the typed text only; no search is sent while typing. Clearing the field
     * drops the applied query so the grid returns to the unsearched list.
     *
     * @param text Text currently in the search bar.
     */
    fun setSearchText(text: String) {
        _searchText.value = text
        if (text.isBlank()) applySearch("")
    }

    /**
     * searchNow
     * Runs the server search. Called only when the user presses Enter, clicks the search icon
     * or picks an autocomplete suggestion.
     *
     * @param text Text to search for.
     */
    fun searchNow(text: String) {
        _searchText.value = text
        applySearch(text)
    }

    /**
     * applySearch
     * Sends [text] to the server as the new query. Does nothing when the trimmed text equals the
     * query already applied, so retyping the same text never refetches.
     *
     * @param text Raw search text; trimmed before use.
     */
    private fun applySearch(text: String) {
        val query = text.trim()
        if (query == appliedQuery) return
        appliedQuery = query
        resetAndFetchFilter()
    }

    // ── Clear all filters ─────────────────────────────────────────────────────

    /**
     * Resets all active chip filters and search text, restoring the general wallpaper list.
     */
    fun clearAllFilters() {
        _selectedTypes.value = emptySet()
        _selectedSeries.value = emptySet()
        _only360.value = false
        _searchText.value = ""
        appliedQuery = ""
        filterJob?.cancel()
        _isLoadingMore.value = false
        filteredWallpapers.clear()
        filterBeforeId = Long.MAX_VALUE
        filterHasMore = true
        isFilterLoading = false
        _hasMoreInCurrentView.value = hasMore
        _screenState.value = buildState()
    }

    // ── Type filter ───────────────────────────────────────────────────────────

    /**
     * Vertical and Phone share a portrait aspect ratio.
     * Selecting Vertical implicitly includes Phone in the API filter.
     */
    private fun effectiveTypes(selected: Set<String>): Set<String> =
        if ("Vertical" in selected) selected + "Phone" else selected

    /**
     * Toggles [type] in/out of the active type filter and re-fetches via the combined filter API.
     *
     * @param type One of: Phone, Desktop, Widescreen, Vertical.
     */
    fun toggleType(type: String) {
        val current = _selectedTypes.value
        _selectedTypes.value = if (type in current) current - type else current + type
        resetAndFetchFilter()
    }

    /**
     * Turns the 360° chip on or off and re-fetches. When on, only wallpapers with a panorama show,
     * combined (AND) with any selected type and series chips.
     */
    fun toggle360() {
        _only360.value = !_only360.value
        resetAndFetchFilter()
    }

    // ── Series filter ─────────────────────────────────────────────────────────

    /**
     * Toggles [series] in/out of the active series filter and re-fetches via the combined filter API.
     *
     * @param series Series name as stored in the tags field.
     */
    fun toggleSeries(series: String) {
        val current = _selectedSeries.value
        _selectedSeries.value = if (series in current) current - series else current + series
        resetAndFetchFilter()
    }

    // ── Filter fetch ──────────────────────────────────────────────────────────

    private fun resetAndFetchFilter() {
        filterJob?.cancel()
        _isLoadingMore.value = false
        filteredWallpapers.clear()
        filterBeforeId = Long.MAX_VALUE
        filterHasMore = true
        isFilterLoading = false
        if (isAnyFilterActive) {
            fetchFilterPage(isInitialLoad = true)
        } else {
            _hasMoreInCurrentView.value = hasMore
            _screenState.value = buildState()
        }
    }

    private fun fetchFilterPage(isInitialLoad: Boolean) {
        if (isFilterLoading && !isInitialLoad) return
        isFilterLoading = true
        filterJob = viewModelScope.launch {
            _isLoadingMore.value = true
            val types = effectiveTypes(_selectedTypes.value).map { it.lowercase() }
            val series = _selectedSeries.value.toList()
            val request = if (appliedQuery.isNotBlank()) {
                repository.searchWallpapers(
                    WallpaperSearchRequest(
                        query = appliedQuery,
                        types = types,
                        series = series,
                        has360 = _only360.value,
                        beforeId = filterBeforeId,
                    )
                )
            } else {
                repository.getFilteredWallpapers(
                    types = types,
                    series = series,
                    has360 = _only360.value,
                    beforeId = filterBeforeId,
                )
            }
            request.collect { state ->
                when (state) {
                    is APIState.Loading -> if (isInitialLoad) _screenState.value = WallpaperState.Loading
                    is APIState.Error -> {
                        isFilterLoading = false
                        _isLoadingMore.value = false
                        _screenState.value = WallpaperState.Error(state.error, state.statusCode)
                    }
                    is APIState.Success -> {
                        isFilterLoading = false
                        _isLoadingMore.value = false
                        val page = state.data
                        filterHasMore = page.size >= 20
                        if (page.isNotEmpty()) filterBeforeId = page.last().id
                        filteredWallpapers.addAll(page.shuffled())
                        _hasMoreInCurrentView.value = filterHasMore
                        _screenState.value = buildState()
                    }
                }
            }
        }
    }

    // ── Load more ─────────────────────────────────────────────────────────────

    /**
     * Called by the scroll handler when the user nears the bottom.
     * Loads the next filter page when filters are active, or the next general page otherwise.
     */
    fun loadMoreIfNeeded() {
        if (isAnyFilterActive) {
            if (filterHasMore && !isFilterLoading) fetchFilterPage(isInitialLoad = false)
        } else {
            if (hasMore && !_isLoadingMore.value) getAllWallpapers(isInitialLoad = false)
        }
    }

    // ── General list ──────────────────────────────────────────────────────────

    /** Resets all state and fetches the first page of wallpapers. */
    fun loadAllWallpapers() {
        accumulatedWallpapers.clear()
        lastId = Long.MAX_VALUE
        hasMore = true
        _hasMoreInCurrentView.value = true
        getAllWallpapers(isInitialLoad = true)
    }

    private fun getAllWallpapers(isInitialLoad: Boolean) {
        viewModelScope.launch {
            _isLoadingMore.value = true
            repository.getAllWallpapers(lastId).collect { response ->
                _screenState.value = when (response) {
                    is APIState.Loading -> {
                        if (isInitialLoad) WallpaperState.Loading else _screenState.value
                    }
                    is APIState.Error -> {
                        _isLoadingMore.value = false
                        WallpaperState.Error(response.error, response.statusCode)
                    }
                    is APIState.Success -> {
                        val page = response.data
                        _isLoadingMore.value = false
                        if (page.size < 20) hasMore = false
                        if (page.isNotEmpty()) lastId = page.last().id
                        accumulatedWallpapers.addAll(page.shuffled())
                        _hasMoreInCurrentView.value = hasMore
                        buildState()
                    }
                }
            }
        }
    }

    // ── Series / Tags loading ─────────────────────────────────────────────────

    private fun loadAllSeries() {
        viewModelScope.launch {
            repository.getAllSeries().collect { state ->
                if (state is APIState.Success) _allSeries.value = state.data.distinct().sorted()
            }
        }
    }

    private fun loadAllTags() {
        viewModelScope.launch {
            repository.getAllTags().collect { state ->
                if (state is APIState.Success) _allTags.value = state.data.distinct().sorted()
            }
        }
    }

    // ── State builder ─────────────────────────────────────────────────────────

    /**
     * Builds the screen state from the correct source list. Text search is done by the server,
     * so no client-side text filtering is applied here.
     */
    private fun buildState(): WallpaperState.Success {
        val result = if (isAnyFilterActive) filteredWallpapers.toList() else accumulatedWallpapers.toList()
        return WallpaperState.Success(
            wallpaperList = result,
            typeList = result.map { it.type }.distinct(),
            hasMore = if (isAnyFilterActive) filterHasMore else hasMore,
        )
    }
}
